import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ChevronDown, ChevronUp, Loader2, Mail, Settings, Sparkles } from "lucide-react";
import { FaFacebook, FaGoogle, FaWhatsapp } from "react-icons/fa";
import {
  CHANNEL_MIXES,
  CURRENCIES,
  Domain,
  OBJECTIVES,
  Objective,
  PLANNER_RATIONALE,
  PRIMARY_KPI,
  SignalCategory,
  fmtCompact,
  fmtMoney,
} from "./data";
import {
  DomainItem,
  DomainSignal,
  UseCaseItem,
  useBudgetPlan,
  useCreatePlannerSegment,
  useDomainSignals,
  useDomainsCatalog,
  usePlanner,
  useSuggestObjectives,
  useUseCases,
} from "./api";

// Audience Studio — ported from the shared HTML prototype into real React
// state/components (kept as a single page, matching how other page-scoped
// builders in this codebase are structured). Use cases, the signal
// recommendation and the budget-aware channel mix are all wired to the real
// AI layer backend (./api.ts: /v1/usecases, /v1/planner, /v1/budget-plan).
// Domains and their signals are no longer a local hardcoded list — the
// backend only actually supports a subset of what data.ts's old DOMAINS
// table had (confirmed live: "unknown domain 'Telecom'. Valid: ['Banking',
// 'Fintech', 'Ecommerce', 'Gaming']"), so both now come straight from
// GET /v1/domains and GET /v1/domains/{domain}/signals — whatever that
// endpoint returns is the whole list, full stop. Objectives/channel-mix
// fallbacks in data.ts stay as-is (data.ts's own OBJECTIVES already matches
// GET /v1/objectives exactly, and CHANNEL_MIXES/PLANNER_RATIONALE/
// PRIMARY_KPI are only ever the offline fallback for /v1/budget-plan, not a
// competing source of truth like DOMAINS was).

const STEPS = [
  { title: "Domain + objective", subtitle: "Set business outcome" },
  { title: "Choose use case", subtitle: "Guide the AI planner" },
  { title: "Review signals", subtitle: "Audience & reach" },
  { title: "Set budget", subtitle: "Channel mix & breakdown" },
];

// Real backend latency (or the instant local fallback) makes the spinner
// flash for a fraction of a second, which reads as broken rather than as
// "thinking" — hold it visible for at least this long.
const MIN_PLANNING_MS = 3000;

// The Destinations grid only has one combined "SMS / Email" card (no
// dedicated Email card), so it maps to SMS — the channel selector on the
// next screen can still be switched to Email by hand.
const CHANNEL_BY_DESTINATION: Record<string, string> = {
  WhatsApp: "whatsapp",
  "SMS / Email": "sms",
};

const CATEGORY_STYLE: Record<SignalCategory, string> = {
  "First-party": "text-primary-700 dark:text-primary-400 bg-primary-50 dark:bg-primary-500/10",
  Behavioral: "text-sky-700 dark:text-sky-400 bg-sky-50 dark:bg-sky-500/10",
  External: "text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-500/10",
  Predictive: "text-fuchsia-700 dark:text-fuchsia-400 bg-fuchsia-50 dark:bg-fuchsia-500/10",
  Derived: "text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-500/10",
};

// Reflects the `source` field the AI layer returns with every response:
// "sarvam" once a real model call succeeded, "fallback" whenever the
// backend (or Sarvam behind it) was unreachable and the built-in sample
// content in data.ts was used instead.
const AiBadge: React.FC<{ live: boolean }> = ({ live }) => (
  <span
    title={live ? "This recommendation came from the live AI planner." : "The AI backend is unavailable right now — showing built-in sample content."}
    className={`text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded ${
      live ? "bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-400" : "bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-400"
    }`}
  >
    {live ? "AI · Live" : "Demo data"}
  </span>
);

const StepIndicator: React.FC<{ n: number; title: string; subtitle: string; active: boolean }> = ({ n, title, subtitle, active }) => (
  <div
    className={`flex-1 min-w-[180px] rounded-xl border px-3.5 py-3 flex items-center gap-2.5 transition-colors ${
      active ? "border-primary-200 dark:border-primary-500/30 bg-primary-50/60 dark:bg-primary-500/10" : "border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900"
    }`}
  >
    <span
      className={`w-7 h-7 shrink-0 rounded-full flex items-center justify-center text-xs font-bold ${
        active ? "bg-primary-600 text-white" : "bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400"
      }`}
    >
      {n}
    </span>
    <div className="min-w-0">
      <p className={`text-sm font-semibold truncate ${active ? "text-gray-900 dark:text-gray-100" : "text-gray-500 dark:text-gray-400"}`}>{title}</p>
      <p className="text-xs text-gray-400 dark:text-gray-500 truncate">{subtitle}</p>
    </div>
  </div>
);

const AudienceStudio: React.FC = () => {
  const navigate = useNavigate();
  // Real GET /v1/domains — the whole list, no local fallback (see the module
  // comment above for why: the backend's actual valid-domains set is
  // narrower than what used to be hardcoded here). `domain` starts empty and
  // is set to the first returned domain once this resolves; every effect
  // below that depends on it guards on it being non-empty first.
  const [domains, setDomains] = useState<DomainItem[]>([]);
  const [domainsLoading, setDomainsLoading] = useState(true);
  const { fetchDomains } = useDomainsCatalog();
  useEffect(() => {
    (async () => {
      const list = await fetchDomains();
      setDomains(list);
      setDomainsLoading(false);
      if (list.length) setDomain(list[0].code);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [domain, setDomain] = useState<string>("");
  const [objective, setObjective] = useState<Objective>("Grow");
  const [prompt, setPrompt] = useState("");
  const [useCase, setUseCase] = useState<string | null>(null);
  const [useCaseOptions, setUseCaseOptions] = useState<UseCaseItem[]>([]);
  // Real GET /v1/domains/{domain}/signals — fetched inside runPlannerWith
  // itself (below) right before each plan, rather than as an independent
  // effect: "Recommended signals" only ever renders once `showResults` is
  // true, which only happens after a planner run completes, so there's
  // nothing to show these for any earlier than that anyway.
  const [signals, setSignals] = useState<DomainSignal[]>([]);
  const { fetchSignals } = useDomainSignals();
  const [showResults, setShowResults] = useState(false);
  const [planning, setPlanning] = useState(false);
  const [selected, setSelected] = useState<number[]>([]);
  const [budget, setBudget] = useState(5000000);
  const [currency, setCurrency] = useState("₦");
  const [audienceName, setAudienceName] = useState("");
  const [aiReason, setAiReason] = useState("");
  const [confidence, setConfidence] = useState<string | null>(null);
  const [sigReasons, setSigReasons] = useState<Record<string, string>>({});
  const [aiLive, setAiLive] = useState<boolean | null>(null);
  const [mix, setMix] = useState(CHANNEL_MIXES.Grow);
  const [rationale, setRationale] = useState(PLANNER_RATIONALE.Grow);
  const [kpi, setKpi] = useState(PRIMARY_KPI.Grow);
  const [budgetPlanning, setBudgetPlanning] = useState(false);
  const [domainsOpen, setDomainsOpen] = useState(false);
  const [activatingDestination, setActivatingDestination] = useState<string | null>(null);
  const resultsRef = useRef<HTMLDivElement>(null);
  const budgetDebounceRef = useRef<number>(0);

  const { loading: useCasesLoading, fetchUseCases } = useUseCases();
  const { requestPlan } = usePlanner();
  const { requestBudgetPlan } = useBudgetPlan();
  const { createSegment } = useCreatePlannerSegment();

  // Real POST /v1/objectives/suggest — per-objective `rationale` for the
  // currently selected domain, shown in place of the static description in
  // the objective picker below once it loads; re-fetched on every domain
  // change. Falls back to the static description (data.ts's OBJECTIVES) if
  // the call fails or hasn't resolved yet, same "real content overrides
  // static fallback" pattern the use-case/planner calls already use.
  const { suggestObjectives } = useSuggestObjectives();
  const [objectiveRationale, setObjectiveRationale] = useState<Record<string, string>>({});
  useEffect(() => {
    if (!domain) return;
    let cancelled = false;
    (async () => {
      const result = await suggestObjectives(domain);
      if (cancelled || !result) return;
      const byCode: Record<string, string> = {};
      result.objectives.forEach((o) => {
        if (o.rationale) byCode[o.code] = o.rationale;
      });
      setObjectiveRationale(byCode);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domain]);

  // Tracks whether results were on screen *before* the domain/objective
  // effect below reacts to a change, so that effect can tell "first pick,
  // nothing to refresh" apart from "changed an input after already seeing
  // a recommendation" — the latter should re-run with a spinner instead of
  // just making the old results vanish.
  const hadResultsRef = useRef(false);
  useEffect(() => {
    hadResultsRef.current = showResults;
  }, [showResults]);

  // Real planner call. Reads domain/objective/useCase/prompt as explicit
  // arguments (not from closure state) so the domain/objective effect below
  // can trigger a re-run with the *new* values in the same tick, rather than
  // racing React's async state updates.
  const runPlannerWith = async (d: Domain, o: Objective, uc: string | null, p: string) => {
    setPlanning(true);
    setShowResults(false);
    // Real GET /v1/domains/{domain}/signals — fetched fresh for whichever
    // domain this run is for (not read from a separately-maintained `signals`
    // state) so a domain change and its plan run always agree on the same
    // list, then also stored for the "Recommended signals" section to render.
    const sigs = await fetchSignals(d);
    setSignals(sigs);
    const startedAt = Date.now();
    const result = await requestPlan(d, o, uc, p);
    const elapsed = Date.now() - startedAt;
    if (elapsed < MIN_PLANNING_MS) {
      await new Promise((resolve) => window.setTimeout(resolve, MIN_PLANNING_MS - elapsed));
    }
    if (result && result.signals.length) {
      const keys = sigs.map((s) => s.key);
      const idxs = result.signals.map((s) => keys.indexOf(s.signal_key)).filter((i) => i >= 0);
      const reasons: Record<string, string> = {};
      result.signals.forEach((s) => {
        reasons[s.signal_key] = s.reason;
      });
      setSelected(idxs.length ? idxs : sigs.map((_, i) => i));
      setSigReasons(reasons);
      setAudienceName(result.audience_name || `${d} · ${o} — Recommended audience`);
      setAiReason(result.reason || "");
      setConfidence(result.confidence || null);
      setAiLive(result.source === "sarvam");
    } else {
      setSelected(sigs.map((_, i) => i));
      setSigReasons({});
      setAudienceName(`${d} · ${o} — Recommended audience`);
      setAiReason(
        `The planner interpreted the ${o.toLowerCase()} objective and selected relevant first-party, behavioral, external and predictive signals before recommending an action and channel plan.`
      );
      setConfidence(null);
      setAiLive(false);
    }
    setPlanning(false);
    setShowResults(true);
    requestAnimationFrame(() => resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }));
  };

  // Switching domain or objective re-fetches use cases + the suggested
  // prompt from the AI layer and resets the picked use case. No local
  // per-domain fallback content anymore (see module comment) — if the call
  // fails there's just an empty use-case list and a generic prompt, never
  // fabricated domain-specific copy. If a recommendation was already
  // showing, changing either input re-runs the planner (with the same
  // loading spinner as a fresh run) instead of silently dropping the old
  // results — anywhere the underlying data is about to refresh should look
  // like it's refreshing.
  useEffect(() => {
    if (!domain) return;
    let cancelled = false;
    setUseCase(null);

    (async () => {
      const result = await fetchUseCases(domain, objective);
      if (cancelled) return;
      const list = result?.use_cases.length ? result.use_cases : [];
      const nextPrompt = result?.suggested_prompt || `Find high-potential customers for a ${objective.toLowerCase()} outcome in ${domain}.`;
      setUseCaseOptions(list);
      setPrompt(nextPrompt);
      if (result) setAiLive(result.source === "sarvam");

      if (hadResultsRef.current) {
        runPlannerWith(domain, objective, null, nextPrompt);
      } else {
        setShowResults(false);
        setPlanning(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domain, objective]);

  const pickUseCase = (uc: UseCaseItem) => {
    setUseCase(uc.name);
    setPrompt(`${uc.name}. Recommend the best audience, signals and action plan for Nigeria.`);
  };

  const runPlanner = () => runPlannerWith(domain, objective, useCase, prompt);

  const toggleSignal = (i: number) => setSelected((prev) => (prev.includes(i) ? prev.filter((x) => x !== i) : [...prev, i]));

  const reach = useMemo(() => {
    const counts = selected.map((i) => signals[i]?.estimated_reach).filter((n): n is number => typeof n === "number");
    if (!counts.length) return 0;
    return Math.round(Math.min(...counts) * (0.78 + Math.min(selected.length, 6) * 0.025));
  }, [selected, signals]);

  const funnel = useMemo(
    () => [
      { label: "Market / customer universe", value: 4_200_000 },
      { label: "Identity-matchable", value: 2_860_000 },
      { label: "Signal-qualified", value: Math.max(reach * 1.35, reach) },
      { label: "Action-ready", value: reach },
    ],
    [reach]
  );

  // Budget-aware channel mix — re-requested (debounced) whenever the
  // budget, currency or estimated reach changes while results are showing.
  useEffect(() => {
    if (!showResults) return;
    setBudgetPlanning(true);
    window.clearTimeout(budgetDebounceRef.current);
    budgetDebounceRef.current = window.setTimeout(async () => {
      const result = await requestBudgetPlan({ domain, objective, use_case: useCase, budget, currency, audience_size: reach });
      if (result) {
        setMix(result.allocations.map((a) => ({ channel: a.channel, pct: a.percent })));
        setRationale(result.rationale || PLANNER_RATIONALE[objective]);
        setKpi(result.primary_kpi || PRIMARY_KPI[objective]);
        setAiLive(result.source === "sarvam");
      } else {
        setMix(CHANNEL_MIXES[objective]);
        setRationale(PLANNER_RATIONALE[objective]);
        setKpi(PRIMARY_KPI[objective]);
      }
      setBudgetPlanning(false);
    }, 700);
    return () => window.clearTimeout(budgetDebounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [budget, currency, reach, domain, objective, useCase, showResults]);

  const notConnected = (destination: string) => toast.info(`${destination} isn't connected yet — this is a demo planner.`);

  // Saves the current recommendation as a real Segment, then hands off to
  // Broadcast with that segment and the clicked channel pre-selected —
  // Broadcast Details (campaign-setup.tsx) reads these back out of
  // localStorage on mount (create-campaign/index.tsx), the same way it
  // already does for draftCampaignId/draftAudienceId.
  const handleActivate = async (destinationName: string) => {
    setActivatingDestination(destinationName);
    const segment = await createSegment();
    setActivatingDestination(null);
    if (!segment) {
      toast.error("Couldn't save this audience as a segment. Please try again.");
      return;
    }
    localStorage.setItem("draftChannel", CHANNEL_BY_DESTINATION[destinationName] || "");
    localStorage.setItem(
      "draftSegment",
      JSON.stringify({
        id: segment.id,
        name: segment.name,
        contacts: segment.contacts,
        validCount: segment.valid_contacts,
        invalidCount: segment.invalid_contacts,
      })
    );
    toast.success(`"${segment.name}" saved — taking you to Broadcast.`);
    navigate("/campaigns/create");
  };

  return (
    <div className="min-h-screen w-full bg-gray-50 dark:bg-gray-950">
      <div className="px-4 sm:px-6 py-6 space-y-5">
        <div>
          <div className="flex items-center gap-2.5 flex-wrap">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Audience Studio</h1>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-3xl">
            Choose your domain and objective, select a use case, then let the AI planner recommend signals, audience size and the best channel mix for your budget.
          </p>
        </div>

        <div className="flex flex-wrap gap-2.5">
          {STEPS.map((s, i) => (
            <StepIndicator key={s.title} n={i + 1} title={s.title} subtitle={s.subtitle} active={i < 2 || showResults} />
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-5 items-start">
          {/* Left rail */}
          <aside className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 p-5 lg:sticky lg:top-6">
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">1. Your domain</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 mb-3">Sets the business vocabulary, signals and benchmarks.</p>
            {domainsLoading ? (
              <div className="flex items-center gap-2 text-xs text-gray-400 dark:text-gray-500 px-1 py-2">
                <Loader2 size={13} className="animate-spin" /> Loading domains…
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2.5">
                {(domainsOpen ? domains : domains.slice(0, 4)).map((d) => {
                  const active = d.code === domain;
                  return (
                    <button
                      key={d.code}
                      type="button"
                      onClick={() => setDomain(d.code)}
                      className={`text-left rounded-xl px-3 py-3 border transition-colors ${
                        active ? "border-2 border-primary-600 bg-primary-50/60 dark:bg-primary-500/10" : "border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 hover:border-primary-300"
                      }`}
                    >
                      <span className="flex items-center gap-1.5 text-sm font-semibold text-gray-900 dark:text-gray-100">
                        {d.icon && <span className="shrink-0">{d.icon}</span>} {d.code}
                      </span>
                      <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">{d.description}</span>
                    </button>
                  );
                })}
              </div>
            )}
            {domains.length > 4 && (
              <button
                type="button"
                onClick={() => setDomainsOpen((o) => !o)}
                className="w-full flex items-center justify-center gap-1.5 mt-2.5 py-2 text-xs font-semibold text-primary-700 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-500/10 rounded-lg transition-colors"
              >
                {domainsOpen ? "View less" : "View more"}
                {domainsOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </button>
            )}

            <div className="mt-5 pt-4 border-t border-gray-100 dark:border-gray-800">
              <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Business objective</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 mb-3">What outcome should the planner optimize for?</p>
              <div className="grid grid-cols-2 gap-2">
                {OBJECTIVES.map((o) => {
                  const active = o.id === objective;
                  return (
                    <button
                      key={o.id}
                      type="button"
                      onClick={() => setObjective(o.id)}
                      className={`text-left rounded-lg px-2.5 py-2 border text-xs transition-colors ${
                        active
                          ? "border-primary-600 bg-primary-50/60 dark:bg-primary-500/10 text-primary-700 dark:text-primary-400 font-semibold"
                          : "border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100"
                      }`}
                    >
                      <span className="block">{o.label}</span>
                      <span className={`block mt-0.5 ${active ? "text-primary-600/80 dark:text-primary-400/80" : "text-gray-400 dark:text-gray-500"}`}>
                        {objectiveRationale[o.id] || o.description}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="mt-5 pt-4 border-t border-gray-100 dark:border-gray-800">
              <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">2. Recommended use cases</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 mb-3">Use cases adapt to the selected objective.</p>
              {useCasesLoading ? (
                <div className="flex items-center gap-2 text-xs text-gray-400 dark:text-gray-500 px-1 py-2">
                  <Loader2 size={13} className="animate-spin" /> Generating use cases…
                </div>
              ) : useCaseOptions.length === 0 ? (
                <p className="text-xs text-gray-400 dark:text-gray-500 px-1 py-2">No use case suggestions available for this domain/objective.</p>
              ) : (
                <div className="space-y-1.5">
                  {useCaseOptions.map((u) => (
                    <button
                      key={u.name}
                      type="button"
                      title={u.description || ""}
                      onClick={() => pickUseCase(u)}
                      className={`w-full text-left px-3 py-2.5 rounded-lg text-sm transition-colors ${
                        useCase === u.name
                          ? "bg-primary-50 dark:bg-primary-500/10 text-primary-700 dark:text-primary-400 font-semibold"
                          : "bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-primary-50 dark:hover:bg-primary-500/10"
                      }`}
                    >
                      → {u.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </aside>

          {/* Main content */}
          <main className="space-y-5 min-w-0">
            <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 p-5">
              <label className="text-sm font-semibold text-gray-900 dark:text-gray-100">Describe what you want the planner to achieve</label>
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                rows={3}
                className="w-full mt-2 px-0 py-1.5 text-base bg-transparent border-0 outline-none resize-y text-gray-900 dark:text-gray-100 placeholder-gray-400"
                placeholder="Example: Find high-value customers in Lagos likely to take a premium personal loan in the next 30 days."
              />
              <div className="flex items-center justify-between gap-3 flex-wrap mt-3 pt-3 border-t border-gray-100 dark:border-gray-800">
                <div className="flex items-center gap-2 flex-wrap">
                  {["Nigeria", "Privacy-aware matching", "AI recommendations"].map((c) => (
                    <span key={c} className="text-xs px-2.5 py-1 rounded-full border border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400">
                      {c}
                    </span>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={runPlanner}
                  disabled={planning || !domain}
                  className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-70 disabled:cursor-not-allowed rounded-lg shadow-sm transition-colors"
                >
                  {planning ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
                  {planning ? "Running planner…" : "Run AI planner"}
                </button>
              </div>
            </div>

            {/* Visible in the main content area (not just the button) so
                switching domain/objective from the left rail gives clear
                feedback even when you're not looking at the button. */}
            {planning && !showResults && (
              <div className="flex flex-col items-center justify-center gap-3 py-16 text-center rounded-2xl border border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900">
                <Loader2 size={26} className="animate-spin text-primary-600" />
                <p className="text-sm text-gray-500 dark:text-gray-400">Refreshing the recommendation for {domain} · {objective}…</p>
              </div>
            )}

            {showResults && (
              <div ref={resultsRef} className="space-y-5">
                {/* Recommendation summary */}
                <div className="grid grid-cols-1 lg:grid-cols-[1.25fr_.75fr] gap-4">
                  <div className="rounded-2xl border border-primary-100 dark:border-primary-500/20 bg-gradient-to-br from-primary-50/60 to-white dark:from-primary-500/10 dark:to-gray-900 p-5">
                    <p className="inline-flex items-center gap-1.5 text-xs font-bold text-primary-700 dark:text-primary-400">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> AI RECOMMENDATION
                      {/* {aiLive !== null && <AiBadge live={aiLive} />} */}
                    </p>
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mt-1.5">{audienceName || `${domain} · ${objective} — Recommended audience`}</h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                      {aiReason ||
                        `The planner interpreted the ${objective.toLowerCase()} objective and selected relevant first-party, behavioral, external and predictive signals before recommending an action and channel plan.`}
                      {confidence && ` (Confidence: ${confidence})`}
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-2.5">
                    {[
                      { v: fmtCompact(reach), l: "Estimated eligible reach*" },
                      { v: String(selected.length), l: "Signals selected" },
                      { v: "3", l: "Signal sources" },
                      { v: confidence || "High", l: "Match confidence" },
                    ].map((m) => (
                      <div key={m.l} className="rounded-xl border border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 p-3.5">
                        <p className="text-xl font-bold text-gray-900 dark:text-gray-100">{m.v}</p>
                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{m.l}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Signals */}
                <div>
                  <div className="flex items-end justify-between mb-3">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Recommended signals</h3>
                    <span className="text-xs text-gray-400 dark:text-gray-500">Toggle signals to refine estimated audience</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
                    {signals.map((s, i) => {
                      const on = selected.includes(i);
                      return (
                        <div
                          key={s.name}
                          className={`rounded-xl border p-4 relative transition-colors ${
                            on ? "border-emerald-200 dark:border-emerald-500/30 bg-white dark:bg-gray-900" : "border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900"
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={on}
                            onChange={() => toggleSignal(i)}
                            className="absolute right-3.5 top-3.5 w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                          />
                          <span
                            className={`inline-block text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ${
                              CATEGORY_STYLE[s.category as SignalCategory] || "text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800"
                            }`}
                          >
                            {s.category}
                          </span>
                          <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 mt-2">{s.name}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 min-h-[32px]">{s.description}</p>
                          <div className="flex items-center justify-between mt-3 text-xs">
                            <span className="text-gray-400 dark:text-gray-500">Available</span>
                            <span className="font-bold text-gray-900 dark:text-gray-100">{fmtCompact(s.estimated_reach)}</span>
                          </div>
                          {sigReasons[s.key] && (
                            <p className="text-xs text-primary-700 dark:text-primary-400 mt-2.5 pt-2.5 border-t border-gray-100 dark:border-gray-800">
                              <span className="font-semibold">Why: </span>
                              {sigReasons[s.key]}
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Funnel */}
                <div className="rounded-2xl border border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 p-5">
                  <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">Estimated audience composition</p>
                  <div className="space-y-2.5">
                    {funnel.map((stage) => (
                      <div key={stage.label} className="grid grid-cols-[1fr_2fr_90px] sm:grid-cols-[190px_1fr_100px] gap-2.5 items-center text-sm">
                        <span className="text-gray-600 dark:text-gray-300 truncate">{stage.label}</span>
                        <div className="h-2.5 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden">
                          <div className="h-full rounded-full bg-primary-600" style={{ width: `${Math.min(100, (stage.value / 4_200_000) * 100)}%` }} />
                        </div>
                        <span className="text-right font-semibold text-gray-900 dark:text-gray-100">{fmtCompact(Math.round(stage.value))}</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-4 leading-relaxed">
                    *Illustrative sample counts. Production counts should be calculated from permissioned provider coverage, identity match rates, freshness and advertiser eligibility.
                  </p>
                </div>

                {/* Budget planner */}
                <div className="rounded-2xl border border-primary-100 dark:border-primary-500/20 bg-gradient-to-br from-primary-50/40 to-white dark:from-primary-500/10 dark:to-gray-900 p-5">
                  <span className="text-[10px] uppercase tracking-wide font-bold text-primary-700 dark:text-primary-400">3. Budget-aware AI campaign planner</span>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mt-1">How much do you want to invest?</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Uses the selected objective, audience and budget to recommend the best channel mix. Sample economics are illustrative.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-[1fr_180px] gap-3.5 mt-4">
                    <div>
                      <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1.5">Total budget</label>
                      <input
                        type="number"
                        min={10000}
                        step={10000}
                        value={budget}
                        onChange={(e) => setBudget(Number(e.target.value) || 0)}
                        className="w-full px-3 py-2.5 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-100 dark:focus:ring-primary-500/20"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1.5">Currency</label>
                      <select
                        value={currency}
                        onChange={(e) => setCurrency(e.target.value)}
                        className="w-full px-3 py-2.5 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-100 dark:focus:ring-primary-500/20"
                      >
                        {CURRENCIES.map((c) => (
                          <option key={c.value} value={c.value}>
                            {c.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_.9fr] gap-3.5 mt-4">
                    <div className="rounded-xl border border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
                      <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2">
                        Recommended channel mix
                        {budgetPlanning && <Loader2 size={13} className="animate-spin text-gray-400" />}
                      </p>
                      <div className={`space-y-2.5 transition-opacity ${budgetPlanning ? "opacity-60" : ""}`}>
                        {mix.map((m, i) => (
                          <div key={m.channel} className="grid grid-cols-[140px_1fr_100px] gap-2.5 items-center text-sm">
                            <span className="text-gray-700 dark:text-gray-200 truncate">
                              {i === 0 && "★ "}
                              {m.channel}
                            </span>
                            <div className="h-2 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden">
                              <div className="h-full rounded-full bg-primary-600" style={{ width: `${m.pct}%` }} />
                            </div>
                            <span className="text-right font-semibold text-gray-900 dark:text-gray-100">
                              {currency}
                              {fmtMoney((budget * m.pct) / 100)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="rounded-xl border border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
                      <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">Planner rationale</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 leading-relaxed">
                        {rationale} The mix should be continuously rebalanced using actual cost, reach and conversion outcomes.
                      </p>
                      <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-800">
                        <span className="text-xs text-gray-400 dark:text-gray-500">Primary KPI</span>
                        <p className="text-sm font-bold text-gray-900 dark:text-gray-100 mt-0.5">{kpi}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Destinations */}
                <div>
                  <div className="flex items-end justify-between mb-3">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Available actions & destinations</h3>
                    <span className="text-xs text-gray-400 dark:text-gray-500">Activation is one possible next action</span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-3">
                    {[
                      { icon: <FaFacebook className="text-[#1877F2]" size={20} />, name: "Meta Ads", sub: "Custom Audience / eligible matching", cta: "Connect" },
                      { icon: <FaGoogle className="text-[#EA4335]" size={20} />, name: "Google Ads", sub: "Customer Match / Data Manager", cta: "Connect" },
                      { icon: <FaWhatsapp className="text-[#25D366]" size={20} />, name: "WhatsApp", sub: "Connect campaigns", cta: "Activate" },
                      { icon: <Mail size={20} className="text-gray-500" />, name: "SMS / Email", sub: "Owned-channel activation", cta: "Activate" },
                      { icon: <Settings size={20} className="text-gray-500" />, name: "API / Export", sub: "CRM, CDP or downstream app", cta: "Configure" },
                    ].map((d) => (
                      <div key={d.name} className="rounded-xl border border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 text-center">
                        <div className="flex justify-center">{d.icon}</div>
                        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 mt-2">{d.name}</p>
                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 min-h-[28px]">{d.sub}</p>
                        <button
                          type="button"
                          // "Activate" is this app's own owned-channel broadcast
                          // (WhatsApp/SMS/Email) — those two save the current
                          // recommendation as a real segment, then send straight
                          // into the real Broadcast flow with that segment and
                          // channel pre-selected. Meta/Google Ads and API/Export
                          // have no integration here yet, so they stay a demo notice.
                          onClick={() => (d.cta === "Activate" ? handleActivate(d.name) : notConnected(d.name))}
                          disabled={activatingDestination === d.name}
                          className="mt-2.5 w-full flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-70 disabled:cursor-not-allowed transition-colors"
                        >
                          {activatingDestination === d.name && <Loader2 size={12} className="animate-spin" />}
                          {activatingDestination === d.name ? "Saving…" : d.cta}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                <p className="text-xs text-gray-400 dark:text-gray-500 leading-relaxed">
                  Consent, provider licensing, permitted purpose, destination policy and retention rules should be enforced before any audience is activated.
                  Sensitive or regulated signals should not be used for prohibited decisioning or advertising purposes.
                </p>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default AudienceStudio;
